/**
 * Палитры Knot — те же 15 тем, что в вебе Knot и в Knot Desktop.
 *
 * ⚠️ Значения СКОПИРОВАНЫ из `Knot/frontend/knot-web/src/lib/themes.ts` как есть,
 * включая палитры, у которых светлота акцента правилась ради контраста. Держать
 * их в синхроне вручную — осознанная плата: это разные репозитории и разные
 * стеки, а генератор ради пятнадцати статических палитр создал бы больше связей,
 * чем экономит. Меняешь палитру в Knot — перенеси сюда (и в ThemeCatalog.cs).
 *
 * Как это применяется — `knotScheme.ts`: цвета раскладываются по ролям Material 3,
 * на которых построен весь интерфейс чата.
 */

export type KnotThemeKind = "dark" | "light";

export type KnotPalette = {
  /** Самая «крайняя» поверхность: у тёмных тем — самая тёмная, у светлых — белая. */
  deep: string;
  /** Фон приложения. */
  bg: string;
  /** Приподнятая поверхность: поле ввода, строка списка. */
  raised: string;
  /** Границы панелей. */
  border: string;
  /** Приглушённый текст. */
  muted: string;
  /** Основной текст. */
  fg: string;
  accent: string;
  /** Текст на залитой акцентом кнопке. */
  accentFg: string;
  warn: string;
  danger: string;
  info: string;
};

export type KnotTheme = {
  id: string;
  label: string;
  kind: KnotThemeKind;
  /** Откуда родом — показывается в подсказке выбора темы. */
  origin: string;
  palette: KnotPalette;
};

export const KNOT_THEMES: KnotTheme[] = [
  // -------------------------------- ТЁМНЫЕ --------------------------------
  {
    id: 'knot-dark',
    label: 'Knot Dark',
    kind: 'dark',
    origin: 'Тёмная по умолчанию — нейтральный графит',
    palette: {
      deep: '#0b0b0e', bg: '#18181b', raised: '#27272a', border: '#3f3f46',
      muted: '#a1a1aa', fg: '#f4f4f5',
      accent: '#10b981', accentFg: '#04140e',
      warn: '#fbbf24', danger: '#f87171', info: '#38bdf8'
    }
  },
  {
    id: 'dark-plus',
    label: 'Dark+ (VS Code)',
    kind: 'dark',
    origin: 'Тема по умолчанию в VS Code',
    palette: {
      deep: '#181818', bg: '#1f1f1f', raised: '#2b2b2b', border: '#3c3c3c',
      muted: '#9d9d9d', fg: '#cccccc',
      // Взят не кнопочный #0078d4 (3.9:1 на фоне сайдбара — текст мылится),
      // а ссылочный синий Dark+ (textLink.foreground) — та же тема, но читаемо.
      accent: '#3794ff', accentFg: '#06213a',
      warn: '#cca700', danger: '#f14c4c', info: '#3794ff'
    }
  },
  {
    id: 'one-dark-pro',
    label: 'One Dark Pro',
    kind: 'dark',
    origin: 'Самая устанавливаемая тема VS Code; родом из Atom',
    palette: {
      deep: '#21252b', bg: '#282c34', raised: '#2c313a', border: '#3e4451',
      muted: '#8f96a3', fg: '#abb2bf',
      accent: '#61afef', accentFg: '#1b1f27',
      warn: '#e5c07b', danger: '#e06c75', info: '#56b6c2'
    }
  },
  {
    id: 'dracula',
    label: 'Dracula',
    kind: 'dark',
    origin: 'Классика с фиолетовым акцентом',
    palette: {
      deep: '#21222c', bg: '#282a36', raised: '#343746', border: '#44475a',
      muted: '#8b95c0', fg: '#f8f8f2',
      accent: '#bd93f9', accentFg: '#21222c',
      warn: '#ffb86c', danger: '#ff5555', info: '#8be9fd'
    }
  },
  {
    id: 'tokyo-night',
    label: 'Tokyo Night',
    kind: 'dark',
    origin: 'Глубокий сине-фиолетовый',
    palette: {
      deep: '#16161e', bg: '#1a1b26', raised: '#24283b', border: '#2f3549',
      muted: '#7b85b0', fg: '#c0caf5',
      accent: '#7aa2f7', accentFg: '#16161e',
      warn: '#e0af68', danger: '#f7768e', info: '#7dcfff'
    }
  },
  {
    id: 'nord',
    label: 'Nord',
    kind: 'dark',
    origin: 'Арктическая палитра — самая спокойная из тёмных',
    palette: {
      deep: '#272c36', bg: '#2e3440', raised: '#3b4252', border: '#4c566a',
      muted: '#9aa5bb', fg: '#eceff4',
      accent: '#88c0d0', accentFg: '#2e3440',
      warn: '#ebcb8b', danger: '#bf616a', info: '#81a1c1'
    }
  },
  {
    id: 'github-dark',
    label: 'GitHub Dark',
    kind: 'dark',
    origin: 'Официальная тёмная тема GitHub',
    palette: {
      deep: '#010409', bg: '#0d1117', raised: '#161b22', border: '#30363d',
      muted: '#8b949e', fg: '#e6edf3',
      // #2f81f7 — это и есть `accent.fg` тёмной темы GitHub (цвет их ссылок),
      // в отличие от кнопочного #1f6feb он не проваливается на почти чёрном фоне.
      accent: '#2f81f7', accentFg: '#041022',
      warn: '#d29922', danger: '#f85149', info: '#58a6ff'
    }
  },
  {
    id: 'monokai',
    label: 'Monokai',
    kind: 'dark',
    origin: 'Тёплая классика из Sublime Text',
    palette: {
      deep: '#1e1f1c', bg: '#272822', raised: '#333428', border: '#49483e',
      muted: '#a59f85', fg: '#f8f8f2',
      accent: '#a6e22e', accentFg: '#1e1f1c',
      warn: '#e6db74', danger: '#f92672', info: '#66d9ef'
    }
  },
  {
    id: 'solarized-dark',
    label: 'Solarized Dark',
    kind: 'dark',
    origin: 'Палитра Итана Шуновера с выверенным контрастом',
    palette: {
      deep: '#00212b', bg: '#002b36', raised: '#073642', border: '#17505f',
      muted: '#93a1a1', fg: '#eee8d5',
      accent: '#268bd2', accentFg: '#00212b',
      warn: '#b58900', danger: '#dc322f', info: '#2aa198'
    }
  },

  // ------------------------------- СВЕТЛЫЕ --------------------------------
  {
    id: 'knot-light',
    label: 'Knot Light',
    kind: 'light',
    origin: 'Светлая по умолчанию — мягкий off-white',
    palette: {
      deep: '#ffffff', bg: '#f7f8fa', raised: '#f1f3f6', border: '#d8dce3',
      muted: '#64748b', fg: '#0f172a',
      // emerald-700 вместо -600: на белом сайдбаре 5.5:1 против 3.8:1.
      accent: '#047857', accentFg: '#ffffff',
      warn: '#b45309', danger: '#dc2626', info: '#0284c7'
    }
  },
  {
    id: 'light-plus',
    label: 'Light+ (VS Code)',
    kind: 'light',
    origin: 'Светлая тема по умолчанию в VS Code',
    palette: {
      deep: '#ffffff', bg: '#f8f8f8', raised: '#f3f3f3', border: '#e5e5e5',
      muted: '#767676', fg: '#3b3b3b',
      accent: '#005fb8', accentFg: '#ffffff',
      warn: '#bf8803', danger: '#e51400', info: '#005fb8'
    }
  },
  {
    id: 'github-light',
    label: 'GitHub Light',
    kind: 'light',
    origin: 'Официальная светлая тема GitHub',
    palette: {
      deep: '#ffffff', bg: '#f6f8fa', raised: '#eef1f4', border: '#d1d9e0',
      muted: '#59636e', fg: '#1f2328',
      accent: '#0969da', accentFg: '#ffffff',
      warn: '#9a6700', danger: '#cf222e', info: '#0969da'
    }
  },
  {
    id: 'one-light',
    label: 'One Light',
    kind: 'light',
    origin: 'Светлая пара к One Dark Pro',
    palette: {
      deep: '#ffffff', bg: '#fafafa', raised: '#f0f0f1', border: '#dcdcdd',
      // Родные #8a8a8d/#4078f2 давали 3.4:1 и 4.1:1 на белом — приглушили светлоту,
      // оттенок оставили: тема узнаётся по нему, а не по яркости.
      muted: '#6f7175', fg: '#383a42',
      accent: '#2a5fd6', accentFg: '#ffffff',
      warn: '#c18401', danger: '#e45649', info: '#0184bc'
    }
  },
  {
    id: 'solarized-light',
    label: 'Solarized Light',
    kind: 'light',
    origin: 'Тёплая бумажная палитра — мягче всех для долгой работы',
    palette: {
      deep: '#fdf6e3', bg: '#f5eed8', raised: '#eee8d5', border: '#ddd6c1',
      // base00 вместо base1: у Solarized светлые оттенки рассчитаны на код, а не
      // на подписи интерфейса — base1 давал 3.1:1 на бумажном фоне.
      muted: '#657b83', fg: '#586e75',
      accent: '#1a6f9e', accentFg: '#fdf6e3',
      warn: '#b58900', danger: '#dc322f', info: '#2aa198'
    }
  },
  {
    id: 'quiet-light',
    label: 'Quiet Light',
    kind: 'light',
    origin: 'Спокойная светлая из поставки VS Code',
    palette: {
      deep: '#ffffff', bg: '#f5f5f5', raised: '#ececec', border: '#dcdcdc',
      muted: '#6b6b6b', fg: '#333333',
      accent: '#3d72b8', accentFg: '#ffffff',
      warn: '#a67f59', danger: '#c94a3f', info: '#448c27'
    }
  }
];

/** Тема по идентификатору; неизвестный id — первая тёмная, как в Knot. */
export function knotThemeById(id: string | undefined | null): KnotTheme {
  return KNOT_THEMES.find((t) => t.id === id) ?? KNOT_THEMES[0];
}

export const DEFAULT_KNOT_THEME_ID = KNOT_THEMES[0].id;
